/**
 * «Акции доставки» (22.10.2026): акции заведений для раздела «Доставка» в
 * приложении. Идущие сегодня стоят каруселью на входе в раздел и строкой на
 * странице заведения; нет ни одной — карусели нет. Только то, что заведение
 * делает для Ақсайым: свои акции с сайтов заведений сюда не переносим.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, DatePicker, Form, Input, Modal, Popconfirm, Select, Space, Switch, Table, Tooltip, Typography, Upload, message } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { deliveryPromosApi, PromoRow } from '../../api/deliveryPromos';
import StatusDot from '../../components/shared/StatusDot';
import { L } from '../../styles/theme';

const { Title, Text } = Typography;

const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const human = (iso: string) => {
  const [, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]}`;
};

const STATE: Record<PromoRow['state'], { color: string; label: string }> = {
  live: { color: L.ok, label: 'Идёт' },
  planned: { color: L.accent, label: 'Запланирована' },
  ended: { color: L.muted, label: 'Закончилась' },
  off: { color: L.muted, label: 'Выключена' },
};

const errText = (e: unknown) =>
  (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Не получилось';

/** Превью как в приложении: фото, логотипа нет, белая плашка с текстом слева внизу. */
const Preview: React.FC<{ r: PromoRow }> = ({ r }) => {
  const img = r.image_url || r.venue_cover;
  return (
    <div
      style={{
        width: 260, height: 142, borderRadius: 14, overflow: 'hidden', position: 'relative',
        background: img ? `url(${img}) center / cover no-repeat` : '#E3E8F0',
        opacity: r.state === 'live' || r.state === 'planned' ? 1 : 0.5,
      }}
    >
      <div style={{ position: 'absolute', left: 8, bottom: 8, maxWidth: 200, background: '#fff', borderRadius: 10, padding: '6px 9px', lineHeight: 1.25 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#0B1A33' }}>{r.title}</div>
        {r.subtitle ? <div style={{ fontSize: 11, color: '#5B6980', marginTop: 2 }}>{r.subtitle}</div> : null}
      </div>
    </div>
  );
};

type FormValues = { venue: string; title: string; subtitle?: string; dates: [Dayjs, Dayjs | null] };

const DeliveryPromosPage: React.FC = () => {
  const [rows, setRows] = useState<PromoRow[]>([]);
  const [venues, setVenues] = useState<{ slug: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [editing, setEditing] = useState<PromoRow | 'new' | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<FormValues>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await deliveryPromosApi.list();
      setRows(res.data.items);
      setVenues(res.data.venues);
    } catch {
      message.error('Не удалось загрузить акции');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const run = async (id: number, fn: () => Promise<unknown>, ok: string) => {
    setBusy(id);
    try {
      await fn();
      message.success(ok);
      await load();
    } catch (e) {
      message.error(errText(e));
    } finally {
      setBusy(null);
    }
  };

  const open = (r: PromoRow | 'new') => {
    setEditing(r);
    form.setFieldsValue(
      r === 'new'
        ? { venue: undefined, title: '', subtitle: '', dates: [dayjs(), null] }
        : { venue: r.venue, title: r.title, subtitle: r.subtitle ?? '', dates: [dayjs(r.starts_on), r.ends_on ? dayjs(r.ends_on) : null] },
    );
  };

  const submit = async () => {
    const v = await form.validateFields();
    const starts = v.dates[0].format('YYYY-MM-DD');
    const ends = v.dates[1] ? v.dates[1].format('YYYY-MM-DD') : null;
    setSaving(true);
    try {
      if (editing === 'new') {
        await deliveryPromosApi.create({ venue: v.venue, title: v.title, subtitle: v.subtitle || null, starts_on: starts, ends_on: ends });
        message.success('Акция добавлена. Теперь загрузите фото');
      } else if (editing) {
        await deliveryPromosApi.update(editing.id, {
          venue: v.venue, title: v.title, subtitle: v.subtitle ?? '', starts_on: starts,
          ...(ends ? { ends_on: ends } : { clear_ends_on: true }),
        });
        message.success('Сохранено');
      }
      setEditing(null);
      await load();
    } catch (e) {
      message.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Как увидит житель',
      key: 'preview',
      width: 290,
      render: (_: unknown, r: PromoRow) => (
        <Space direction="vertical" size={6}>
          <Preview r={r} />
          <Space size={6}>
            <Upload
              accept="image/png,image/jpeg,image/webp"
              showUploadList={false}
              beforeUpload={(file) => {
                void run(r.id, () => deliveryPromosApi.uploadImage(r.id, file as File), 'Фото загружено');
                return false;
              }}
            >
              <Button size="small" icon={<UploadOutlined />} loading={busy === r.id}>
                {r.image_url ? 'Заменить фото' : 'Загрузить фото'}
              </Button>
            </Upload>
            {r.image_url ? (
              <Popconfirm title="Убрать фото? Будет обложка заведения." onConfirm={() => run(r.id, () => deliveryPromosApi.clearImage(r.id), 'Фото убрано')}>
                <Tooltip title="Убрать фото">
                  <Button size="small" danger icon={<DeleteOutlined />} disabled={busy === r.id} />
                </Tooltip>
              </Popconfirm>
            ) : null}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Акция',
      key: 'title',
      render: (_: unknown, r: PromoRow) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.title}</div>
          {r.subtitle ? <Text type="secondary">{r.subtitle}</Text> : null}
          <div style={{ marginTop: 4 }}>{r.venue_name}</div>
        </div>
      ),
    },
    {
      title: 'Когда',
      key: 'dates',
      width: 200,
      render: (_: unknown, r: PromoRow) => (
        <Space direction="vertical" size={4}>
          <StatusDot color={STATE[r.state].color} label={STATE[r.state].label} />
          <Text>{r.ends_on ? `с ${human(r.starts_on)} по ${human(r.ends_on)}` : `с ${human(r.starts_on)}, до снятия`}</Text>
        </Space>
      ),
    },
    {
      title: 'Показывать',
      key: 'active',
      width: 110,
      render: (_: unknown, r: PromoRow) => (
        <Switch checked={r.is_active} loading={busy === r.id} onChange={(v) => void run(r.id, () => deliveryPromosApi.update(r.id, { is_active: v }), v ? 'Включена' : 'Выключена')} />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_: unknown, r: PromoRow) => (
        <Space>
          <Tooltip title="Изменить"><Button icon={<EditOutlined />} onClick={() => open(r)} /></Tooltip>
          <Popconfirm title="Удалить акцию?" okText="Удалить" cancelText="Отмена" onConfirm={() => run(r.id, () => deliveryPromosApi.remove(r.id), 'Удалено')}>
            <Tooltip title="Удалить"><Button danger icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>Акции доставки</Title>
          <Text type="secondary">
            Акции заведений для раздела «Доставка». Идущие сегодня стоят каруселью на входе в раздел и строкой на странице
            заведения. Нет ни одной, и карусели нет. Фото горизонтальное, от 1200×650: слева внизу ляжет белая плашка с
            текстом. Без фото будет обложка заведения. В приложении изменения появятся в течение пяти минут.
          </Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => open('new')}>Новая акция</Button>
      </Space>
      <Table
        style={{ marginTop: 16 }}
        rowKey="id"
        dataSource={rows}
        columns={columns}
        loading={loading}
        pagination={false}
        size="middle"
        locale={{ emptyText: 'Акций пока нет' }}
      />
      <Modal
        open={editing !== null}
        title={editing === 'new' ? 'Новая акция' : 'Акция'}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={saving}
        onOk={() => void submit()}
        onCancel={() => setEditing(null)}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="venue" label="Заведение" rules={[{ required: true, message: 'Выберите заведение' }]}>
            <Select options={venues.map((v) => ({ value: v.slug, label: v.name }))} placeholder="Заведение" />
          </Form.Item>
          <Form.Item name="title" label="Заголовок" rules={[{ required: true, min: 2, message: 'Коротко, что за акция' }]} extra="Коротко: «Плов по средам −15%»">
            <Input maxLength={80} showCount />
          </Form.Item>
          <Form.Item name="subtitle" label="Подпись" extra="Условия одной строкой: «До 31 октября, при заказе от 5 000 ₸»">
            <Input maxLength={140} showCount />
          </Form.Item>
          <Form.Item name="dates" label="Даты показа" rules={[{ required: true, message: 'Укажите дату начала' }]} extra="Без даты окончания акция идёт до снятия">
            <DatePicker.RangePicker format="D MMMM YYYY" allowEmpty={[false, true]} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DeliveryPromosPage;
